/**
 * Observability — Sentry (optional) + global error hook
 * SSOT: synaxion-core/09-observability/
 */

import { logger } from '@shared/logger';
import { flushMetrics } from './metrics-exporters';
import { bootstrapMetricsExporters } from './bootstrap-metrics-exporters';
import { maskPiiInString, maskPiiValue } from './pii';

let sentryReady = false;
let warnedMissingDsn = false;

const DEFAULT_APP_VERSION = '3.0.0';

type SentryEvent = {
  message?: string;
  transaction?: string;
  user?: { email?: string; username?: string; ip_address?: string };
  breadcrumbs?: Array<{ message?: string; data?: Record<string, unknown> }>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  tags?: Record<string, string>;
};

function maskPiiInEvent(event: SentryEvent): SentryEvent {
  const masked: SentryEvent = { ...event };

  if (masked.message) {
    masked.message = maskPiiInString(masked.message);
  }
  if (masked.transaction) {
    masked.transaction = maskPiiInString(masked.transaction);
  }
  if (masked.user) {
    masked.user = {
      ...masked.user,
      ...(masked.user.email ? { email: '[email-redacted]' as const } : {}),
      ...(masked.user.username ? { username: maskPiiInString(masked.user.username) } : {}),
    };
  }
  if (masked.breadcrumbs) {
    masked.breadcrumbs = masked.breadcrumbs.map((crumb) => ({
      ...crumb,
      ...(crumb.message ? { message: maskPiiInString(crumb.message) } : {}),
      ...(crumb.data ? { data: maskPiiValue(crumb.data) as Record<string, unknown> } : {}),
    }));
  }
  if (masked.extra) {
    masked.extra = maskPiiValue(masked.extra) as Record<string, unknown>;
  }
  if (masked.contexts) {
    masked.contexts = maskPiiValue(masked.contexts) as Record<string, unknown>;
  }
  if (masked.tags) {
    const tags: Record<string, string> = {};
    for (const [key, tagValue] of Object.entries(masked.tags)) {
      tags[key] = maskPiiInString(tagValue);
    }
    masked.tags = tags;
  }

  return masked;
}

function resolveTracesSampleRate(): number {
  const raw = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE?.trim();
  if (raw) {
    const parsed = Number.parseFloat(raw);
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 1) {
      return parsed;
    }
    logger.warn('Observability: VITE_SENTRY_TRACES_SAMPLE_RATE 무효 — 기본값 사용', {
      data: { raw },
    });
  }
  return import.meta.env.MODE === 'production' ? 0.1 : 1.0;
}

function resolveRelease(): string {
  const fromEnv = import.meta.env.VITE_APP_VERSION?.trim();
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_APP_VERSION;
}

export async function initObservability(): Promise<void> {
  bootstrapMetricsExporters();

  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) {
    if (!warnedMissingDsn) {
      console.warn(
        '[Observability] VITE_SENTRY_DSN 미설정 — Sentry 비활성. 구조화 로거만 사용합니다.'
      );
      warnedMissingDsn = true;
    }
    logger.info('Observability: Sentry DSN 없음 — 구조화 로거만 사용');
  } else {
    try {
      const Sentry = await import('@sentry/react');
      const environment =
        import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE || 'development';

      Sentry.init({
        dsn,
        environment,
        release: `inflomatrix@${resolveRelease()}`,
        tracesSampleRate: resolveTracesSampleRate(),
        beforeSend(event) {
          return maskPiiInEvent(event as SentryEvent) as typeof event;
        },
      });
      sentryReady = true;
      logger.info('Observability: Sentry 초기화 완료', {
        data: { environment, release: resolveRelease() },
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.warn('[Observability] @sentry/react 초기화 실패:', err.message);
      logger.warn('Observability: @sentry/react 미설치 또는 init 실패', {
        error: err.message,
      });
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      flushMetrics();
    });
    const flushIntervalMs = 60_000;
    window.setInterval(() => flushMetrics(), flushIntervalMs);
  }

  window.addEventListener('unhandledrejection', (event) => {
    logger.error(
      'Unhandled rejection',
      event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    );
    if (sentryReady) {
      void import('@sentry/react').then((Sentry) => {
        Sentry.captureException(event.reason);
      });
    }
  });
}

export function isSentryEnabled(): boolean {
  return sentryReady;
}
